import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';

import { useListMomentsArtifactNotesQuery } from '@/store/api/momentsApi';

const noteArg = (artifactId: string | undefined) =>
  artifactId ? { artifactId } : skipToken;

/**
 * Per-reflection saved-highlight counts for the home module.
 *
 * `artifact.listMine` carries no highlight count, so home fans out to
 * `artifact_note.list?artifact_id=…` once per visible reflection row. That
 * handler scopes to `owner_person_id = <viewer> AND artifact_id = <row>`, the
 * same predicate behind the footer's "All Highlights (n)" search, so a row count
 * and the global total can never disagree.
 *
 * Each slot is a fixed hook call so hook order stays stable regardless of how
 * many rows came back; empty slots pass skipToken and issue no request. The slot
 * count must cover HOME_REFLECTION_LIMIT — `useMemberReflectionHighlightCounts`
 * tests assert the two stay in step.
 */
export const HOME_HIGHLIGHT_COUNT_SLOTS = 3;

export const useMemberReflectionHighlightCounts = (
  artifactIds: string[],
): Record<string, number> => {
  const first = useListMomentsArtifactNotesQuery(noteArg(artifactIds[0]));
  const second = useListMomentsArtifactNotesQuery(noteArg(artifactIds[1]));
  const third = useListMomentsArtifactNotesQuery(noteArg(artifactIds[2]));

  const firstCount = first.data?.notes.length;
  const secondCount = second.data?.notes.length;
  const thirdCount = third.data?.notes.length;

  return useMemo(() => {
    const counts: Record<string, number> = {};
    [firstCount, secondCount, thirdCount].forEach((count, index) => {
      const artifactId = artifactIds[index];
      if (artifactId && count !== undefined) {
        counts[artifactId] = count;
      }
    });
    return counts;
  }, [artifactIds, firstCount, secondCount, thirdCount]);
};
